
import { timer as observableTimer, Subscription, ReplaySubject, Subject, Observable, interval, BehaviorSubject } from 'rxjs';
import { share } from 'rxjs/operators';
import { Viewer } from './../components/analytics/realtime-audience/models/viewer.model';
import { SapiService } from './sapi.service';
import { WebSocketsService } from './web-sockets.service';
import { ViewSession } from './../components/analytics/realtime-audience/models/view-session.model';
import { ViewSessionEvent } from './../components/analytics/realtime-audience/models/view-session-event.model';
import { Injectable, OnDestroy, Renderer2, RendererFactory2 } from '@angular/core';

import { NavigationStart, Router } from '@angular/router';

const replayMaxTime = 30 * 1000;

const fpsLevel1 = 10;
const fpsLevel2 = 20;
const fpsLevel3 = 30;
const fpsLevel4 = 40;

@Injectable()
export class RealtimeService implements OnDestroy {
	public viewSessions: ViewSession[];
	public viewers: Viewer[];
	public server: string;
	public viewerCountsObs: BehaviorSubject<any>;

	private viewSessionsRef: any;
	private viewersRef: any;
	private _clipRef: any;
	private _orphanedViewSessionsRef: any;
	private _eventAdded: ReplaySubject<ViewSessionEvent>;
	private _viewerAdded: ReplaySubject<Viewer>;
	private _viewerRemoved: ReplaySubject<Viewer>;
	private _socketSub: Subscription;
	private _sapiSub: Subscription;
	private _lastKnowPub: string;

	private fromDate: Date;
	private wsSubscription: Observable<any>;
	private _wsSubscriptionN: any;
	private _backlogInterval: Subscription;
	private _removeInterval: Subscription;
	private _initialCallFinished: ReplaySubject<boolean>;
	private _lostFocus: ReplaySubject<boolean>;
	private _ignoreEvents: string[];
	private _eventBuffer: ViewSessionEvent[];
	private _detailLevel: number;
	private _measuredFPS: number;
	private _pollBacklogSub: Subscription;

	private renderer: Renderer2;

	private isSubscribed: boolean;
	private timer: Observable<number>;
	private overlayTimer: Subscription;
	private _viewerCounts: any;

	constructor(
		private webSocketsService: WebSocketsService,
		private sapiService: SapiService,
		private router: Router,
		private rendererFactory: RendererFactory2
	) {
		this._viewerCounts = { viewers: 0, mobileViewers: 0 };
		this.viewSessions = [];
		this.viewers = [];
		this.viewSessionsRef = {};
		this._clipRef = {};
		this._orphanedViewSessionsRef = {};
		this.viewersRef = {};
		this._eventBuffer = [];
		this._wsSubscriptionN = {};
		this.isSubscribed = false;

		this.timer = observableTimer(90 * 1000);

		let hidden: string; let visibilityChange: string;
		const doc = document as any;
		if (typeof document.hidden !== 'undefined') { // Opera 12.10 and Firefox 18 and later support
			hidden = 'hidden';
			visibilityChange = 'visibilitychange';
		} else if (typeof doc.msHidden !== 'undefined') {
			hidden = 'msHidden';
			visibilityChange = 'msvisibilitychange';
		} else if (typeof doc.webkitHidden !== 'undefined') {
			hidden = 'webkitHidden';
			visibilityChange = 'webkitvisibilitychange';
		}

		this.renderer = this.rendererFactory.createRenderer(null, null);

		this.renderer.listen('document', visibilityChange, (event: any) => {
			if (doc[hidden]) {
				this.setDetailLevel(1);
				if (this.overlayTimer) {
					this.overlayTimer.unsubscribe();
					this.overlayTimer = null;
				}
				this.overlayTimer = this.timer.subscribe(() => {
					this.isSubscribed = false;
					this.unsubscribeAll();
					this._lostFocus.next(true);
				});
			} else {
				if (this.overlayTimer) {
					this.overlayTimer.unsubscribe();
					this.overlayTimer = null;
				}
				this.setDetailLevel(5);
				if (!this.isSubscribed) {
					this._lostFocus.next(false);
					this._getBacklog();
				}
			}
		});

		this._viewerAdded = new ReplaySubject(null, replayMaxTime);
		this._eventAdded = new ReplaySubject(null, replayMaxTime);
		this._viewerRemoved = new ReplaySubject(null, replayMaxTime);
		this._initialCallFinished = new ReplaySubject(1);
		this._lostFocus = new ReplaySubject(1);
		this.viewerCountsObs = new BehaviorSubject(null);
		this._ignoreEvents = ''.split(' '); // pg pa se rs
		this.fromDate = (new Date());
		this.fromDate.setHours(this.fromDate.getHours() - 1); // last 2 hours
		this._getBacklog();
		this._startBacklogPoll();

		this.router.events.subscribe((routeEvent) => {
			if (routeEvent instanceof NavigationStart) {
				if (!this.wsSubscription && routeEvent.url.endsWith('realtime')) {
					this._getBacklog();
				} else if (this.wsSubscription && !routeEvent.url.endsWith('realtime')) {
					this.unsubscribeAll();
				}
			}
		});
	}

	ngOnDestroy() {
		// todo
		this._eventAdded.unsubscribe();
		this._viewerAdded.unsubscribe();
		this._viewerRemoved.unsubscribe();
		this._removeInterval.unsubscribe();
		if (this._pollBacklogSub) {
			this._pollBacklogSub.unsubscribe();
		}
		if (this._backlogInterval) {
			this._backlogInterval.unsubscribe();
		}
		if (this._sapiSub) {
			this._sapiSub.unsubscribe();
		}
		if (this._socketSub) {
			this._socketSub.unsubscribe();
		}
		if (this.wsSubscription) {
			this.webSocketsService.unsubscribeFromChannel('rtstats');
			for (let i = 1; i <= 5; i++) {
				if (this._wsSubscriptionN[i]) {
					this.webSocketsService.unsubscribeFromChannel('rtstatsFullDetail' + i);
					this._wsSubscriptionN[i] = null;
				}
			}
		}
	}

	unsubscribeAll() {
		this.webSocketsService.unsubscribeFromChannel('rtstats');
		for (let i = 1; i <= 5; i++) {
			if (this._wsSubscriptionN[i]) {
				this.webSocketsService.unsubscribeFromChannel('rtstatsFullDetail' + i);
				this._wsSubscriptionN[i] = null;
			}
		}
		if (this._backlogInterval) {
			this._backlogInterval.unsubscribe();
			this._backlogInterval = null;
		}
		if (this._removeInterval) {
			this._removeInterval.unsubscribe();
			this._removeInterval = null;
		}
		if (this._sapiSub) {
			this._sapiSub.unsubscribe();
			this._sapiSub = null;
		}
		if (this._socketSub) {
			this._socketSub.unsubscribe();
			this._socketSub = null;
		}
		this.viewSessions = [];
		this.viewers = [];
		this.viewSessionsRef = {};
		this._orphanedViewSessionsRef = {};
		this.viewersRef = {};
		this._eventBuffer = [];
		this._wsSubscriptionN = {};

		this.wsSubscription = null;

		this._viewerAdded = new ReplaySubject(null, replayMaxTime);
		this._eventAdded = new ReplaySubject(null, replayMaxTime);
		this._viewerRemoved = new ReplaySubject(null, replayMaxTime);
	}

	set measuredFPS(val: number) {
		if (this.overlayTimer) {
			return;
		}
		this._measuredFPS = val;
		switch (true) {
			case (this.viewers.length > 750):
				this.setDetailLevel(0);
				break;
			case (this.viewers.length < 40):
				this.setDetailLevel(5);
				break;
			case (this._measuredFPS >= 0 && this._measuredFPS < fpsLevel1):
				this.setDetailLevel(1);
				break;
			case (this._measuredFPS >= fpsLevel1 && this._measuredFPS < fpsLevel2):
				this.setDetailLevel(2);
				break;
			case (this._measuredFPS >= fpsLevel2 && this._measuredFPS < fpsLevel3):
				this.setDetailLevel(3);
				break;
			case (this._measuredFPS >= fpsLevel3 && this._measuredFPS < fpsLevel4):
				this.setDetailLevel(4);
				break;
			case (this._measuredFPS >= fpsLevel4):
				this.setDetailLevel(5);
				break;

		}
	}

	setDetailLevel(n: number): void {
		for (let i = 1; i <= 5; i++) {
			if (i <= n) {
				if (!this._wsSubscriptionN[i]) {
					this._wsSubscriptionN[i] = this.webSocketsService.subscribeToChannel('rtstatsFullDetail' + i);
					this._wsSubscriptionN[i].subscribe((message: any) => {
						const ev = message as ViewSessionEvent;
						this._eventBuffer.push(ev);
					});
				}
			} else {
				if (this._wsSubscriptionN[i]) {
					this.webSocketsService.unsubscribeFromChannel('rtstatsFullDetail' + i);
					this._wsSubscriptionN[i] = null;
				}

			}
		}

		this._detailLevel = n;
	}

	eventsFromWebsocket(): Observable<ViewSessionEvent> {
		if (!this.wsSubscription) {
			this.wsSubscription = this.webSocketsService.subscribeToChannel('rtstats');
		}
		const eventObservable = new Subject<ViewSessionEvent>();

		this.wsSubscription.subscribe(message => {
			const ev = message as ViewSessionEvent;
			// console.warn('ev', ev);
			// eventObservable.next(ev);
			this._eventBuffer.push(ev);
		});

		this._backlogInterval = interval(2000).subscribe(() => {
			this._eventBuffer.sort((a: ViewSessionEvent, b: ViewSessionEvent) =>
				// reverse sort for reverse loop:
				Number(b.ts) - Number(a.ts)
			);
			// console.warn('sorted this._eventBuffer ', JSON.stringify(this._eventBuffer), new Date());
			let i = this._eventBuffer.length;
			// reverse loop:
			while (i--) {
				const ev = this._eventBuffer[i];
				eventObservable.next(ev);
				// console.warn('ev from buffer ', ev);
				this._eventBuffer.splice(i, 1);
			}
		});

		return eventObservable.pipe(share());
	}

	public addEvent(ev: ViewSessionEvent) {
		ev = this._prepareEvent(ev);
		// console.warn('ev', ev);
		// check for Viewer
		if (ev.uid) {
			if (!this.viewersRef[ev.uid]) { // && (ev.ev === 'xst' && ev.et === 'View') || ev.action === 'add') {
				if (ev.action !== 'remove') {
					// add as new viewer
					this.viewersRef[ev.uid] = new Viewer(ev);
					this.viewers.push(this.viewersRef[ev.uid]);

					// viewerAdded Observable
					this._viewerAdded.next(this.viewersRef[ev.uid]);
				} else {
					// console.warn( 'REMOVE WITHOUT SESSION ev ', ev)
					return;
				}

			} else {
				if (ev.action === 'add') { // add of a previously active viewer which got removed
					this.viewers.push(this.viewersRef[ev.uid]);

					// viewerAdded Observable
					// this.viewersRef[ev.uid].isRemoved = false;
					this.viewersRef[ev.uid].reActivated = true;
					this._viewerAdded.next(this.viewersRef[ev.uid]);
				}


				// update existing viewer
				const viewer: Viewer = this.viewersRef[ev.uid] as Viewer;
				if (viewer) {
					viewer.update(ev);
				}

				if (this.viewersRef[ev.uid].hadRemove) {
					// console.warn('ev after hadRemove', ev, this.viewersRef[ev.uid]);
				}
			}
			// console.warn('this.viewersRef[ev.uid]', this.viewersRef[ev.uid]);
		}

		// check for ViewSession
		if (!this.viewSessionsRef[ev.sid] && ev.sid) {
			this.viewSessionsRef[ev.sid] = new ViewSession(this.sapiService);

			// console.warn('this.viewSessionsRef[ ' + ev.sid , ev.uid, this.viewSessionsRef[ev.sid]);

			if (this.viewersRef[ev.uid]) {
				this.viewersRef[ev.uid].addViewSession(this.viewSessionsRef[ev.sid]);
			} else {
				this._orphanedViewSessionsRef[ev.sid] = this.viewSessionsRef[ev.sid];
			}
			this.viewSessions.unshift(this.viewSessionsRef[ev.sid]);
		}

		if (this._orphanedViewSessionsRef[ev.sid] && this.viewersRef[ev.uid]) {
			// console.warn('added orphanedViewSession to viewersRef', this._orphanedViewSessionsRef[ev.sid], this.viewersRef[ev.uid]);
			this.viewersRef[ev.uid].addViewSession(this.viewSessionsRef[ev.sid]);
			if (this._orphanedViewSessionsRef[ev.sid].events) {
				this._orphanedViewSessionsRef[ev.sid].events.forEach((vse: ViewSessionEvent) => {
					this.viewersRef[ev.uid].update(vse);
				});
			}
			delete this._orphanedViewSessionsRef[ev.sid];
		}

		if (this.viewSessionsRef[ev.sid]) {
			this.viewSessionsRef[ev.sid].addEvent(ev);
		}

		// // ignoring historical views for now
		// if (ev.viewedClips && Object.keys(ev.viewedClips).length) {
		// 	Object.keys(ev.viewedClips).forEach( (key: any ) => {
		// 		let viewedClip = ev.viewedClips[key];
		// 		if (key !== ev.id && viewedClip) {
		// 			let addEv = this._prepareEvent({
		// 				fromBacklog: true,
		// 				ts: ev.ts,
		// 				to: viewedClip.to,
		// 				action: 'add',
		// 				id: key,
		// 				ct: viewedClip.ct || 'Clip ' + key,
		// 				sid: ev.viewSessionId + '_' + key,
		// 				viewSessionId: ev.viewSessionId
		// 			});
		// 			console.warn(ev.ev + ' ========== addEv', addEv, ev);
		// 			this.addEvent(addEv);
		// 		}
		// 	});
		// 	// setTimeout(() => {
		// 		// eventAdded Observable after the previous clips
		// 		console.warn(ev.ev + ' ========== _eventAdded in timeout', ev);
		// 		this._eventAdded.next(ev);
		// 	// }, 1)
		// } else {
		// 	// eventAdded Observable
		// 		console.warn(ev.ev + ' ========== _eventAdded', ev);
		// 	this._eventAdded.next(ev);
		// }

		this._eventAdded.next(ev);

		if (ev.action === 'remove') {
			// TODO! We get the viewSessionId from the backend and need to relate this to the viewer (uid).
			// We probably only want to set a flag that this user is inactive, not remove it completely.
			// viewerRemoved Observable
			const ref = ev.viewSessionId;
			if (this.viewersRef[ref]) {
				if (!this.viewersRef[ref].hadRemove) {
					this._viewerRemoved.next(this.viewersRef[ref]);
				}
				this.viewersRef[ref].hadRemove = true;
			} else {
				console.warn('Remove without ref! ev ', ev);
			}

			// removed user object?
			// setTimeout( () => {
			// 	let vi = this.viewers.indexOf( this.viewersRef[ref] );
			// 	console.warn( 'viewer index ', vi, this.viewersRef[ref] );
			// });
		}

	}

	_checkForMc(ev: ViewSessionEvent) {

		if (ev.id && ev.ct) {
			this._clipRef['c' + ev.id] = ev.ct;
		} else if (ev.id && Number(ev.id) && !ev.ct && this._clipRef['c' + ev.id]) {
			ev.ct = this._clipRef['c' + ev.id];
		} else if (ev.id && Number(ev.id) && !this._clipRef['c' + ev.id]) {
			// console.warn('title not found for ', ev);
		}
	}

	get initialCallFinished() {
		return this._initialCallFinished;
	}

	get lostFocus() {
		return this._lostFocus;
	}

	get eventAdded() {
		return this._eventAdded; // .asObservable();
	}

	get viewerRemoved() {
		return this._viewerRemoved; // .asObservable();
	}

	get viewerAdded() {
		return this._viewerAdded; // .asObservable();
	}

	private _prepareEvent(ev: any): ViewSessionEvent {
		// if (ev.vu) {
		// 	ev.uid = ev.vu;
		// }
		if (ev.viewSessionId) {
			ev.uid = ev.viewSessionId;
		} else if (ev.sid) {
			// ev.uid = ev.sid;
		}

		if (ev.ts) {
			ev.timestamp = Number(ev.ts);
			ev.time = new Date(ev.timestamp);
		} else if (ev.timestamp) {
			ev.timestamp = Number(ev.timestamp);
			ev.time = new Date(ev.timestamp);
		}

		if (ev.fromBacklog === true) {
			ev.action = 'add';
			ev.topic = 'concurrentViewSessions';
			if (ev.currentClipView) {
				Object.keys(ev.currentClipView).forEach((key) => {
					ev[key] = ev.currentClipView[key];
				});
			}
		}
		if (ev.action === 'add') {
			ev.ev = 'add';
		}

		if (ev.pp) {
			this._lastKnowPub = ev.pp;
		}

		if (ev && !ev.pp && this._lastKnowPub) {
			ev.pp = this._lastKnowPub;
		}

		this._checkForMc(ev);
		return (ev as ViewSessionEvent);
	}

	private _removeViewers() {
		// console.warn('_removeViewers');
		let i = this.viewers.length;
		// console.warn('this.viewers length = ', i);
		while (i--) {
			const viewer = this.viewers[i];
			if (viewer && viewer.isRemoved) {
				this.viewers.splice(i, 1);
				// console.warn('removing viewer ', i, viewer, this.viewers.length);
				if (viewer.uid && this.viewersRef[viewer.uid]) {
					// setTimeout(() => {
					// 	delete this.viewersRef[viewer.uid];
					// }, 1000);
				}
			}
		}

		// let dl = 5 - Math.round(this.viewers.length / 100);
		// this.setDetailLevel(dl);
	}

	private _startBacklogPoll() {
		if (!this._pollBacklogSub) {
			this._pollBacklogSub = interval(5000).subscribe(() => {
				this.sapiService.getUrl('/rtstats/concurrentViewSessions').subscribe((backlogData) => {
					this._viewerCounts.viewers = backlogData.concurrentViewSessions || 0;
					this._viewerCounts.mobileViewers = backlogData.concurrentMobileViewSessions || 0;
					this.viewerCountsObs.next(this._viewerCounts);
					// console.warn('this._viewerCounts', this._viewerCounts);
				});
			});
		}

	}

	private _getBacklog(): void {
		this.isSubscribed = true;

		this._sapiSub = this.sapiService.getUrl('/rtstats').subscribe((backlogData) => {
			// console.warn('backlogData', backlogData);

			this._removeInterval = interval(5000).subscribe(() => {
				this._removeViewers();
			});

			const backlogEvents = backlogData.sessions;
			if (backlogEvents) {
				backlogEvents.forEach((ev: any) => {
					ev.fromBacklog = true;
					this.addEvent(ev);
				});
			}

			this._socketSub = this.eventsFromWebsocket().subscribe((ev) => {
				if (this._ignoreEvents.indexOf(ev.ev) === -1) {
					this.addEvent(ev);
				}
			});
			this.setDetailLevel(5);
			this._initialCallFinished.next(true);
		});
	}
}
